import React, { useEffect, useMemo, useState } from 'react';
import { Appointment, MedicalCertificate, SystemConfig } from '@/types';
import { useAppointmentMutations } from '@features/admin/data';
import { Calendar, Filter, MailCheck, Printer } from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { Pagination } from '@shared/components/common/Pagination';
import { formatUkDate } from '@/lib/dates';
import { toTitleCase } from '@/lib/questionnaireReview';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openBlankPrintWindow,
  writeCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { api } from '@/services/api';

type HistoryStatusFilter = 'all' | 'booked' | 'completed' | 'noshow' | 'cancelled';
type HistoryOutcomeFilter = 'all' | 'fit' | 'fit_restrictions' | 'referred' | 'unfit' | 'pending';

interface CustomerHistoryPanelProps {
  customerId: string;
  systemConfig?: SystemConfig;
  selectedHistoryId: string | null;
  onSelectHistory: (id: string) => void;
  onAppointmentResolved: (appointment: Appointment | null) => void;
  onOpenReview?: () => void;
}

function getOutcomeChipClass(outcome?: string | null): string {
  switch (String(outcome || '').toLowerCase()) {
    case 'fit':
      return 'bg-green-50 text-green-700 border-green-100';
    case 'fit_restrictions':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    case 'referred':
      return 'bg-orange-50 text-orange-700 border-orange-100';
    case 'unfit':
      return 'bg-red-50 text-red-700 border-red-100';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

export { getOutcomeChipClass };
export { toTitleCase } from '@/lib/questionnaireReview';

export const CustomerHistoryPanel: React.FC<CustomerHistoryPanelProps> = ({
  customerId,
  systemConfig,
  selectedHistoryId,
  onSelectHistory,
  onAppointmentResolved,
  onOpenReview
}) => {
  const appointmentMutations = useAppointmentMutations();

  const [items, setItems] = useState<Appointment[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [historySearch, setHistorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<HistoryStatusFilter>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<HistoryOutcomeFilter>('all');

  useEffect(() => {
    setPage(1);
    setHistorySearch('');
    setStatusFilter('all');
    setOutcomeFilter('all');
  }, [customerId]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    api
      .listStaffCustomerHistory(customerId, { page, pageSize })
      .then((data) => {
        if (!isActive) return;
        setItems(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setItems([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [customerId, page, pageSize]);

  const normalizedSearch = historySearch.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((app) => {
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      const outcome = String(app.certificateSummary?.outcome || 'pending').toLowerCase();
      if (outcomeFilter !== 'all' && outcome !== outcomeFilter) return false;
      if (!normalizedSearch) return true;
      const index = [
        app.id,
        app.customerName,
        app.companyName,
        app.serviceName,
        app.venueName,
        app.time,
        app.status,
        app.certificateSummary?.outcome
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return index.includes(normalizedSearch);
    });
  }, [items, statusFilter, outcomeFilter, normalizedSearch]);

  const counts = useMemo(
    () => ({
      completed: items.filter((i) => i.status === 'completed').length,
      withCertificate: items.filter((i) => i.certificateSummary?.id).length,
      pendingReview: items.filter((i) => i.status === 'completed' && !i.certificateSummary?.id)
        .length
    }),
    [items]
  );

  // Auto-select first visible item
  useEffect(() => {
    if (!filteredItems.length) {
      onAppointmentResolved(null);
      return;
    }
    if (!selectedHistoryId || !filteredItems.some((i) => i.id === selectedHistoryId)) {
      onSelectHistory(filteredItems[0].id);
    }
  }, [filteredItems, selectedHistoryId, onSelectHistory, onAppointmentResolved]);

  // Resolve the full Appointment object for the selected history item
  useEffect(() => {
    const found = filteredItems.find((i) => i.id === selectedHistoryId) || null;
    onAppointmentResolved(found);
  }, [selectedHistoryId, filteredItems, onAppointmentResolved]);

  const handleResendCertEmail = async (appId: string) => {
    const confirmed = await showConfirmModal(
      "Re-send the 'Certificate Issued' email to this patient?",
      {
        title: 'Resend Certificate Email',
        confirmText: 'Resend',
        cancelText: 'Cancel',
        isDestructive: false
      }
    );
    if (!confirmed) return;
    try {
      await appointmentMutations.sendReminder(appId, 'email', 'certificate_issued');
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to resend certificate email.'));
    }
  };

  const handleViewCertificate = async (app: Appointment) => {
    if (!systemConfig) return;
    const printWindow = openBlankPrintWindow();
    if (!printWindow) {
      showAlertModal(
        'Your browser blocked the certificate window. Please allow pop-ups for this site and try again.'
      );
      return;
    }
    let cert: MedicalCertificate | null = null;
    try {
      const result = await api.listStaffCertificates({
        appointmentId: app.id,
        page: 1,
        pageSize: 1
      });
      cert = result.items?.[0] || null;
    } catch (err: unknown) {
      printWindow.close();
      showAlertModal(normalizeErrorMessage(err, 'Failed to load certificate.'));
      return;
    }
    if (!cert) {
      printWindow.close();
      showAlertModal('Certificate not found for this appointment.');
      return;
    }
    const html = buildCertificateHtml({
      template: systemConfig.templates.certificateTemplate.body,
      systemConfig,
      appointment: app,
      certificate: cert,
      clientName: resolveCertificateClientName(app)
    });
    writeCertificatePrintWindow(
      html,
      buildCertificateFileName({ appointment: app, certificate: cert }),
      printWindow
    );
  };

  return (
    <div>
      <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
        <Calendar className="w-4 h-4" /> Clinical History & Outcomes
      </h4>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5" /> Review Filters
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <input
            type="text"
            placeholder="Search history..."
            className="w-full border rounded-lg px-3 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
          />
          <select
            className="w-full border rounded-lg px-2 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as HistoryStatusFilter)}
          >
            <option value="all">All statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="noshow">No show</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            className="w-full border rounded-lg px-2 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value as HistoryOutcomeFilter)}
          >
            <option value="all">All outcomes</option>
            <option value="fit">Fit</option>
            <option value="fit_restrictions">Fit with restrictions</option>
            <option value="referred">Referred</option>
            <option value="unfit">Unfit</option>
            <option value="pending">Pending certificate</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200">
            Completed: {counts.completed}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-teal-50 text-teal-700 border border-teal-100">
            Certified: {counts.withCertificate}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-100">
            Awaiting review: {counts.pendingReview}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <p className="text-slate-500 italic text-sm">Loading history...</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500 italic text-sm">No booking history found.</p>
        ) : filteredItems.length === 0 ? (
          <p className="text-slate-500 italic text-sm">No records match the review filters.</p>
        ) : (
          filteredItems.map((app) => {
            const cert = app.certificateSummary;
            const isSelected = selectedHistoryId === app.id;
            return (
              <div
                key={app.id}
                className={`bg-white border rounded-xl p-4 shadow-sm group transition-all ${
                  isSelected ? 'border-teal-400 ring-2 ring-teal-100' : 'hover:border-teal-300'
                }`}
              >
                <div className="flex justify-between items-start mb-2 gap-3">
                  <div>
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      {formatUkDate(app.date, '—')}
                      <span className="text-slate-200">|</span>
                      <span className="text-sm font-medium">{app.time}</span>
                    </div>
                    {app.companyName && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        Company: {app.companyName}
                      </div>
                    )}
                    {app.serviceName && (
                      <div className="text-[10px] text-slate-500">Service: {app.serviceName}</div>
                    )}
                    <div className="text-xs font-bold text-teal-700 mt-1 uppercase tracking-tighter">
                      ID: {app.id}
                    </div>
                  </div>
                  <StatusBadge
                    status={app.status}
                    className="text-[9px] tracking-widest px-2 py-0.5"
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-slate-50 flex items-center justify-between gap-2">
                  <span
                    className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg border ${getOutcomeChipClass(cert?.outcome || 'pending')}`}
                  >
                    {toTitleCase(String(cert?.outcome || 'pending'))}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        onSelectHistory(app.id);
                        onOpenReview?.();
                      }}
                      className={`px-2.5 py-1 text-[10px] rounded-lg border font-black uppercase tracking-wider transition-all ${
                        isSelected
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'
                      }`}
                      title="Open Medical Data Review"
                    >
                      Review
                    </button>
                    {cert && (
                      <>
                        <button
                          onClick={() => handleResendCertEmail(app.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Resend Certificate Email"
                        >
                          <MailCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewCertificate(app)}
                          className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                          title="Print/Download Certificate"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoading}
      />
    </div>
  );
};
